<template>
  <speakol-summary-card>

    <!-- title -->
    <template v-slot:title>
      <div class="title md:title">{{ $t('advertisers_fund.order_summary.title') }}</div>
    </template>

    <!-- body -->
    <template v-slot:body>
      <div>
        <!-- Input Amount -->
        <div class="flex items-baseline mb-4">
          <div class="w-1/2 amount-label md:amount-label">{{ $t('advertisers_fund.order_summary.amount') }}</div>
          <div class="w-1/2 flex justify-end">
            <div class="amount md:amount">{{ (amount || 0) | addComma('$') }}</div>
          </div>
        </div>

        <!-- Dicscount -->
        <div v-if="discount > 0" class="flex items-baseline mb-4">
          <div class="w-1/2 amount-label md:amount-label discount">{{ $t('advertisers_fund.order_summary.discount') }}</div>
          <div class="w-1/2 flex justify-end">
            <div class="amount md:amount discount">{{ discount | addComma('$') }}</div>
          </div>
        </div>

        <!-- Sub Total -->
        <div class="flex items-baseline mb-4">
          <div class="w-1/2 amount-label md:amount-label">{{ $t('advertisers_fund.order_summary.sub_total') }}</div>
          <div class="w-1/2 flex justify-end">
            <div class="amount md:amount">{{ subTotal | addComma('$')}}</div>
          </div>
        </div>

        <!-- Fees -->
        <div v-if="!isAdmin" class="flex items-baseline mb-4">
          <div class="w-12 mr-4 amount-label">{{ $t('advertisers_fund.order_summary.fees') }}</div>
          <span class="w-20 fees text-speakol-color text-base">
              {{ `${paymentMethodFees.value || 0}%` }}
          </span>
          <div class="w-full flex justify-end">
            <div class="amount md:amount">{{ (fees || 0) | addComma('$') }}</div>
          </div>
        </div>
      </div>

      <!-- VAT -->
      <div class="flex items-baseline">
        <div class="w-12 amount-label mr-4 md:amount-label">{{ $t('advertisers_fund.order_summary.vat') }}</div>
        <span class="w-12 percentage md:percentage text-base">{{ `${vatPercentage || 0}%` }}</span>
        <div class="w-full flex justify-end">
          <div class="amount md:amount">{{ vat | addComma('$') }}</div>
        </div>
      </div>
    </template>

    <!-- footer -->
    <template v-slot:footer>
      <div class="flex items-baseline mb-2">
        <div class="w-1/2 amount-total-label md:amount-total-label">{{ $t('advertisers_fund.order_summary.paid_amount') }}</div>
        <div class="w-1/2 flex justify-end">
          <div class="amount-total md:amount-total">{{ totalPaid | addComma('$') }}</div>
        </div>
      </div>

      <div v-if="bonus > 0" class="flex items-baseline">
        <div class="w-1/2 balance-amount-label bonus pb-2">{{ $t('advertisers_fund.order_summary.bonus') }}</div>
        <div class="w-1/2 flex justify-end">
          <div class="balance-amount md:balance-amount">{{ bonus | addComma('$') }}</div>
        </div>
      </div>

      <div class="flex items-baseline">
        <div class="w-1/2 balance-amount-label text-base">{{ $t('advertisers_fund.order_summary.amount_added_to_balance_label') }}</div>
        <div class="w-1/2 flex justify-end">
          <div class="balance-amount md:balance-amount">{{ (balance || 0) | addComma('$') }}</div>
        </div>
      </div>
    </template>

  </speakol-summary-card>
</template>


<script>
import {get} from 'lodash'

export default {
  props: {
    isAdmin: {
      default: false
    },

    amount: {
      required: true
    },

    account: {
      type: Object,
      default () {
        return this.$store.getters['authentication/activeAccount']
      }
    },
    paymentMethod: String
  },

  data () {
    // TODO: get from api
    return {
      feesTable: {
        'Stripe': {
          value: 3
        },
        'Paytabs': {
          value: 2.7
        },
        'Paypal': {
          value: 5
        },
        'Checkout': {
          value: 3
        }
      }
    }
  },

  computed: {
    paymentMethodFees () {
      if (!this.isAdmin) {
        return this.feesTable[this.paymentMethod] ? this.feesTable[this.paymentMethod] : this.feesTable['stripe']
      }

      return 0
    },

    subTotal () {
      if (this.discount > 0) {
        return (this.amount - this.discount)
      }

      return this.amount || 0
    },

    isVatEnabled () {
      return this.isEligableForVAT()
    },

    vat () {
      if (this.isVatEnabled) {
        if (this.isAdmin) {
          return this.subTotal - (this.subTotal / (1 + (this.vatPercentage / 100))) // 1.05 = 1 + 5 / 100 >> vat equation
        }

        return (this.calcPercentageValue(parseFloat(this.subTotal) + parseFloat(this.fees), this.vatPercentage))
      }

      return 0
    },

    bonusPercentage () {
      if (this.isEligableForFreeBonus())  {
        return parseFloat(get(this.account, 'bonus_percentage', 0))
      }

      return 0
    },

    bonus () {
      if (
        this.account &&
        this.account.bonus_type === this.$constants.BONUS_TYPES.BONUS &&
        this.isEligableForFreeBonus()
      ) {
        return this.calcBonusValue()
      }

      return 0
    },

    discount () {
      if (
        this.account &&
        this.account.bonus_type === this.$constants.BONUS_TYPES.DISCOUNT &&
        this.isEligableForFreeBonus()
      ) {
        return this.calcBonusValue()
      }

      return 0
    },

    fees () {
      if (!this.isAdmin) {
        return this.calcPercentageValue(
          this.subTotal,
          this.paymentMethodFees.value || 0
        )
      }

      return 0
    },

    vatPercentage () {
      if (this.isEligableForVAT())  {
        return parseFloat(this.account.actual_vat_percentage)
      }

      return 0
    },

    totalPaid () {
      if (this.isAdmin) {
        return this.amount - this.discount
      }

      return this.calcRegularAccountTotalPaid()
    },

    balance () {
      let balance = parseFloat(this.amount) // in case of discount or not

      if (this.isAdmin && this.isVatEnabled) { // in case is admin and vat enabled
        balance =  this.amount - this.vat
      }

      if (this.bonus > 0) {
        balance += parseFloat(this.bonus) //in case of bonus
      }

      return parseFloat(balance)
    }
  },

  methods: {
    calcRegularAccountTotalPaid () {
      return (parseFloat(this.subTotal) + parseFloat(this.fees) + parseFloat(this.vat))
    },

    calcBonusValue () {
      return parseFloat(this.amount * this.bonusPercentage / 100)
    },

    calcPercentageValue (totalValue, percentage) {
      return totalValue * percentage / 100
    },

    isEligableForVAT () {
      const paymentMehtodsHaveVAT = [
        this.$constants.PAYMENT_METHODS.STRIPE,
        this.$constants.PAYMENT_METHODS.PAYPAL,
        this.$constants.PAYMENT_METHODS.PAYTABS,
        this.$constants.PAYMENT_METHODS.CHECKOUT,
        this.$constants.PAYMENT_METHODS.CASH,
        this.$constants.PAYMENT_METHODS.BANK
      ]

      if (
        this.paymentMethod &&
        this.account &&
        this.account.actual_vat_percentage > 0 &&
        paymentMehtodsHaveVAT.includes(this.paymentMethod)
      ) {
        return true
      }

      return false
    },

    isEligableForFreeBonus () {
      const paymentMehtodsHaveBonus = [
        this.$constants.PAYMENT_METHODS.STRIPE,
        this.$constants.PAYMENT_METHODS.PAYPAL,
        this.$constants.PAYMENT_METHODS.PAYTABS,
        this.$constants.PAYMENT_METHODS.CHECKOUT,
        this.$constants.PAYMENT_METHODS.CASH,
        this.$constants.PAYMENT_METHODS.BANK
      ]

      if (
        this.paymentMethod &&
        this.account &&
        this.account.bonus_percentage !== 0 &&
        paymentMehtodsHaveBonus.includes(this.paymentMethod)
      ) {
        return true
      }

      return false
    }
  }
}

</script>


<style lang="scss" scoped>

.title {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #2c2c2c;
}

.amount-label {
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 21px;
  color: #2c2c2c;
}

.amount {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #2c2c2c;
}

.amount-total-label {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #2c2c2c;
}

.amount-total {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  color: #2c2c2c;
}

.balance-amount-label {
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 18px;
  color: #2c2c2c;

  @apply whitespace-no-wrap;
}

.balance-amount {
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 18px;
  color: #2c2c2c;
}

.percentage {
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 19px;
  color: #0092d7;
}

.bonus {
  color: #21ba45 !important;
}

.discount {
  font-style: normal;
  font-weight: normal;
  color: #db2828 !important;
}

@media (min-width: 768px) {
  .md\:title {
    font-style: normal;
    font-weight: 500;
    font-size: 24px;
    line-height: 24px;
  }

  .md\:amount-label {
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 21px;
    color: #2c2c2c;
  }

  .md\:amount {
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    color: #2c2c2c;
  }

  .md\:amount-total-label {
    font-style: normal;
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    color: #2c2c2c;
  }

  .md\:amount-total {
    font-style: normal;
    font-weight: 500;
    font-size: 18px;
    line-height: 24px;
    color: #2c2c2c;
  }

  .md\:balance-amount {
    font-style: normal;
    font-weight: normal;
    font-size: 12px;
    line-height: 18px;
    color: #2c2c2c;
  }

  .md\:percentage {
    font-style: normal;
    font-weight: normal;
    font-size: 13px;
    line-height: 19px;
    color: #0092d7;
  }
}
</style>
